/**
 * Planung zurücknehmen (G-42): kontrollierte Umkehrung von „Schichtplan
 * erstellen" — DELETE /shift-plan löscht Tages-Planzeilen UND Wochenpläne des
 * Schichtmodells im KW-Bereich. 3 Schritte: Bereich wählen → Bestätigung mit
 * deutlichem Warntext → Ergebnis inkl. blocked-Tabelle.
 *
 * Anwesenheits-Guard: Planzeilen mit bereits gebuchter Anwesenheit (gleicher
 * Mitarbeiter + Tag + Schicht) bleiben stehen und werden als `blocked`
 * zurückgemeldet; Wochenpläne fallen nur, wenn alle ihre Tageszeilen weg sind.
 *
 * Gate: personal_schreiben (bewusst nicht das Hart-Lösch-Set — die gebuchte
 * Realität schützt der Guard; durchgesetzt serverseitig).
 */

import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertTriangle } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { fmtDate } from "@/lib/format"
import { isoWeekOf, todayIso, weeksInIsoYear } from "@/lib/isoWeek"
import { api, apiError } from "@/services/api"
import type { ShiftPlanRetractResponse } from "@/types"

type Step = "form" | "confirm" | "result"

interface RetractShiftPlanDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RetractShiftPlanDialog({ open, onOpenChange }: RetractShiftPlanDialogProps) {
  const queryClient = useQueryClient()
  const current = isoWeekOf(todayIso())
  const [shiftModelId, setShiftModelId] = useState("")
  const [year, setYear] = useState(String(current.year))
  const [fromWeek, setFromWeek] = useState(String(current.week))
  const [toWeek, setToWeek] = useState(String(current.week))
  const [step, setStep] = useState<Step>("form")
  const [result, setResult] = useState<ShiftPlanRetractResponse | null>(null)

  useEffect(() => {
    if (open) {
      setStep("form")
      setResult(null)
    }
  }, [open])

  const models = useQuery({
    queryKey: ["shiftModels", { size: 100 }],
    queryFn: () => api.organization.shiftModels.list({ size: 100 }),
    enabled: open,
  })
  const modelName = (models.data?.data ?? []).find((m) => m.id === shiftModelId)?.name

  const maxWeek = weeksInIsoYear(Number(year) || current.year)
  const valid =
    shiftModelId !== "" &&
    Number(year) >= 2000 &&
    Number(fromWeek) >= 1 &&
    Number(toWeek) <= maxWeek &&
    Number(fromWeek) <= Number(toWeek)

  const retract = useMutation({
    mutationFn: () =>
      api.planning.retract({
        shiftModelId: Number(shiftModelId),
        year: Number(year),
        fromWeek: Number(fromWeek),
        toWeek: Number(toWeek),
      }),
    onSuccess: (r) => {
      setResult(r)
      setStep("result")
      queryClient.invalidateQueries({ queryKey: ["weekPlans"] })
      queryClient.invalidateQueries({ queryKey: ["shiftPlan"] })
      queryClient.invalidateQueries({ queryKey: ["rotationStatus"] })
      toast.success(
        `Planung zurückgenommen: ${r.deletedEntries} Tageszeilen, ${r.deletedWeekPlans} Wochenpläne gelöscht`
      )
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const bereich = `${modelName ?? "—"}, KW ${fromWeek}–${toWeek}/${year}`

  // Grid-Spalten der blocked-Tabelle: header als String (Spalten-Auswahl),
  // Datum sortiert ueber den ISO-String, gerendert im deutschen Format
  const blockedColumns = useMemo<ColumnDef<ShiftPlanRetractResponse["blocked"][number]>[]>(
    () => [
      {
        id: "date",
        header: "Datum",
        accessorFn: (b) => b.date,
        cell: ({ row }) => fmtDate(row.original.date),
        meta: { headClassName: "w-28", cellClassName: "tabular-nums" },
      },
      {
        id: "employee",
        header: "Mitarbeiter",
        accessorFn: (b) => b.employeeName,
      },
      {
        id: "reason",
        header: "Grund",
        accessorFn: (b) => b.reason,
        meta: { cellClassName: "text-muted-foreground" },
      },
    ],
    []
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === "result" ? "Planung zurückgenommen" : "Planung zurücknehmen"}
          </DialogTitle>
          <DialogDescription>
            {step === "form" &&
              "Umkehrung von „Schichtplan erstellen“: löscht Tages-Planzeilen UND Wochenpläne des Schichtmodells im gewählten KW-Bereich."}
            {step === "confirm" && "Bitte bestätigen Sie die Rücknahme."}
            {step === "result" &&
              "Tage mit bereits gebuchter Anwesenheit wurden geschützt und blieben stehen."}
          </DialogDescription>
        </DialogHeader>

        {/* ------------------------- Schritt 1: Bereich ------------------------- */}
        {step === "form" && (
          <div className="grid gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Schichtmodell *</Label>
              <Select value={shiftModelId} onChange={(e) => setShiftModelId(e.target.value)}>
                <option value="">— Schichtmodell wählen —</option>
                {(models.data?.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.shiftType})
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Jahr *</Label>
                <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>KW von *</Label>
                <Input
                  type="number"
                  min={1}
                  max={maxWeek}
                  value={fromWeek}
                  onChange={(e) => setFromWeek(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>KW bis *</Label>
                <Input
                  type="number"
                  min={1}
                  max={maxWeek}
                  value={toWeek}
                  onChange={(e) => setToWeek(e.target.value)}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Geschützt bleiben Tage, an denen bereits Anwesenheiten gebucht sind — diese
              Planzeilen werden NICHT gelöscht und im Ergebnis aufgelistet.
            </p>
          </div>
        )}

        {/* ----------------------- Schritt 2: Bestätigung ----------------------- */}
        {step === "confirm" && (
          <div className="grid gap-4 py-2">
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <div className="space-y-1">
                <p className="font-medium">
                  Alle Tages-Planzeilen und Wochenpläne für {bereich} werden gelöscht.
                </p>
                <p>
                  Das betrifft den kompletten Bereich — auch bereits kommunizierte Pläne.
                  Einzige Ausnahme: Tage mit bereits gebuchter Anwesenheit bleiben stehen.
                  Die Aktion kann nicht rückgängig gemacht werden (neu erstellen ist
                  jederzeit möglich).
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------- Schritt 3: Ergebnis ------------------------- */}
        {step === "result" && result && (
          <div className="grid gap-4 py-2">
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="font-medium">Ergebnis</p>
              <p className="text-muted-foreground">
                {result.deletedEntries} Tageszeilen und {result.deletedWeekPlans} Wochenpläne
                gelöscht · {result.blocked.length} Zeilen geschützt
              </p>
            </div>
            {result.blocked.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  An diesen Tagen sind bereits Anwesenheiten gebucht — nicht gelöscht:
                </p>
                <div className="max-h-48 overflow-y-auto rounded-md border p-2">
                  {/* Ergebnis-Dialog: keine Persistenz noetig — bewusst ohne storageKey */}
                  <DataGrid
                    columns={blockedColumns}
                    data={result.blocked}
                    emptyMessage="Keine geschützten Zeilen."
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === "form" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button variant="destructive" disabled={!valid} onClick={() => setStep("confirm")}>
                Zurücknehmen …
              </Button>
            </>
          )}
          {step === "confirm" && (
            <>
              <Button variant="outline" onClick={() => setStep("form")}>
                Zurück
              </Button>
              <Button
                variant="destructive"
                disabled={retract.isPending}
                onClick={() => retract.mutate()}
              >
                Ja, Planung endgültig zurücknehmen
              </Button>
            </>
          )}
          {step === "result" && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Schließen
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
